import { useState } from "react";
import { fmtDate, dateKey } from "../utils/helpers";
import "../styles/home-surface.css";

/**
 * Seven days at a glance.
 *
 * Deliberately not a streak counter and not a fraction out of seven — a gap in
 * the row has to read as a quiet day, never as a debt. Days you moved are the
 * accent; days you logged something else are a faint mark; empty days are the
 * same soft grey as the track behind them.
 */
export default function WeeklyDotTracker({ weeklyData, weekActiveDays, workouts, mealLog, runs }) {
  const [expandedDay, setExpandedDay] = useState(null);

  return (
    <section aria-label="Your last seven days">
      <div className="ds-row-between" style={{ alignItems: "baseline" }}>
        <p className="hs-key hs-key--flush">Your week</p>
        {/* A read of the week, not a second count of it. The line below this row
            already says how many days in a row you've shown up; putting "6 days
            with something logged" 20px above "4 days in a row" gave the reader
            two different numbers for the same seven dots and left them to do the
            arithmetic — the opposite of restful. One number on this row, and it
            is the warmer sentence. */}
        <p className="ds-meta">
          {weekActiveDays === 0
            ? "A quiet week so far"
            : weekActiveDays >= 5
              ? "A steady week"
              : weekActiveDays >= 3
                ? "A gentle week"
                : "A quieter week"}
        </p>
      </div>

      <div className="hs-week-row" style={{ marginTop: "var(--stack-sm)" }}>
        {weeklyData.map((d, i) => {
          const state = d.hasWorkout ? "moved" : d.hasMeal ? "logged" : "quiet";
          const isExpanded = expandedDay === i;
          const summary = d.hasWorkout
            ? d.hasMeal
              ? "you moved and logged meals"
              : "you moved"
            : d.hasMeal
              ? "meals logged"
              : "a quiet day";
          return (
            <button
              key={i}
              type="button"
              className="hs-week-day"
              data-today={d.isToday}
              aria-expanded={isExpanded}
              aria-label={`${d.label}: ${summary}`}
              onClick={() => setExpandedDay(isExpanded ? null : i)}
            >
              <span aria-hidden="true">{d.shortLabel}</span>
              <span className="hs-week-dot" data-state={state} aria-hidden="true" />
            </button>
          );
        })}
      </div>

      {expandedDay !== null && weeklyData[expandedDay] && (
        <DayDetail
          day={weeklyData[expandedDay]}
          workouts={workouts}
          mealLog={mealLog}
          runs={runs}
          onClose={() => setExpandedDay(null)}
        />
      )}
    </section>
  );
}

function DayDetail({ day, workouts, mealLog, runs, onClose }) {
  const dayWorkouts = (workouts || []).filter((w) => {
    const d = new Date(w.startTime);
    return d.toDateString() === day.date?.toDateString();
  });

  const dayRuns = (runs || []).filter((r) => {
    const d = new Date(r.timestamp);
    return d.toDateString() === day.date?.toDateString();
  });

  // mealLog is keyed by LOCAL date (dateKey); toISOString() is UTC and would
  // look up the wrong day in the evening for users west of UTC.
  const dateStr = dateKey(day.date);
  const dayMeals = dateStr && mealLog ? (mealLog[dateStr] || []).filter((m) => m.mealId !== "__water__") : [];

  const hasContent = dayWorkouts.length > 0 || dayRuns.length > 0 || dayMeals.length > 0;

  return (
    <div className="hs-day-detail">
      <div className="hs-day-detail-head">
        <p className="ds-meta">
          {day.date ? fmtDate(day.date.getTime()) : day.label}
          {day.isToday ? " · today" : ""}
        </p>
        <button
          type="button"
          className="btn-ghost ds-tap-target"
          onClick={onClose}
          aria-label="Close day detail"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {!hasContent && <p className="ds-support">A quiet day. Those matter too.</p>}

      {dayWorkouts.map((w) => {
        const dur = w.endTime ? Math.round((w.endTime - w.startTime) / 60000) : null;
        return (
          <p key={w.id} className="ds-support hs-day-line">
            {w.name}
            {dur ? ` · ${dur} min` : null}
          </p>
        );
      })}

      {dayRuns.map((r) => (
        <p key={r.id} className="ds-support hs-day-line">
          {r.runType} run · {r.distance.toFixed(1)} {r.unit || "km"}
        </p>
      ))}

      {dayMeals.length > 0 && (
        <p className="ds-support hs-day-line">
          {dayMeals.length} meal{dayMeals.length !== 1 ? "s" : ""} logged
        </p>
      )}
    </div>
  );
}
